import React, { memo, useCallback, useMemo } from "react";

import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import { getTagMention } from "modules/utils/Functions";
import colors from "app/colors";
import { RestoreIcon, GreenCheckIcon } from "modules/icons";
import Toast from "modules/toasts/toast";
import { useTags } from "modules/providers/tags-provider";
import {
  AdminTagsSetActiveMutation,
  AdminTagsSetActiveMutationVariables,
} from "gql/graphql";
import ADMIN_TAGS_SET_ACTIVE_MUTATION from "./_networking/admin-tags-set-active";
import { useOverlay } from "modules/providers/overlay-provider";
import useDisplayToast from "modules/hooks/use-display-toast";

interface IProps {
  tagIds: string[];
}

const AdminConfirmUnarchiveTagModal = ({ tagIds }: IProps) => {
  const { closeModal } = useOverlay();
  const { tags: data } = useTags();
  const { displayToast } = useDisplayToast();
  const [adminTagsSetActiveApi] = useMutation<
    AdminTagsSetActiveMutation,
    AdminTagsSetActiveMutationVariables
  >(ADMIN_TAGS_SET_ACTIVE_MUTATION);

  const tags = useMemo(
    () => data?.filter((tag) => tagIds.includes(tag.id)),
    [data, tagIds],
  );

  const tagsFirstThree = tags.slice(0, 3);

  const onUnarchiveClicked = useCallback(() => {
    adminTagsSetActiveApi({
      variables: {
        active: true,
        idList: tagIds,
      },
    });
    displayToast(() => (
      <Toast
        message={`${tagIds.length} Tags Unarchived`}
        leftIcon={<RestoreIcon boxSize={5} fill={colors.grayMain} />}
      />
    ));
    closeModal();
  }, [adminTagsSetActiveApi, closeModal, tagIds, displayToast]);

  return (
    <Modal isOpen onClose={closeModal} size="xs" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader pt={8}>
          <VStack spacing={6}>
            <GreenCheckIcon />
            <Text fontSize={26}>Unarchive Tag{tags.length > 1 && "s"}?</Text>
          </VStack>
        </ModalHeader>
        <ModalBody pb={6}>
          <VStack spacing={6}>
            <Text fontSize={16} color={colors.gray50} textAlign="center">
              Are you sure you want to unarchive:
              <Text>
                {tagsFirstThree.map((tag, index) => (
                  <React.Fragment key={tag.id}>
                    {index < 3 && index > 0 && ", "}
                    {getTagMention(tag)}
                  </React.Fragment>
                ))}
                {tags.length > 3 ? `, and ${tags.length - 3} more?` : "?"}
              </Text>
            </Text>
            <HStack>
              <Button variant="cancel" height={8} w={28} onClick={closeModal}>
                No
              </Button>
              <Button height={8} w={28} onClick={onUnarchiveClicked}>
                Unarchive
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(AdminConfirmUnarchiveTagModal);
